import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfig } from '@config/app.config';
import { Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { DmagCoreService } from './dmag-core.service';
import { DmagPopAlertService } from './dmag-popalert.service';
import { DmagStorageService } from './dmag-storage.service';

@Injectable()
export class DmagHttpInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private coreService: DmagCoreService,
    private popAlertService: DmagPopAlertService,
    private storageService: DmagStorageService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Se sta caricando il file locale delle traduzioni
    if (request.url.indexOf('i18n') > -1) {
      return next.handle(request);
    }

    // Imposta il base url
    const baseUrl = AppConfig.apiUrl;
    let modifiedRequest = request.clone({ url: baseUrl + request.url });

    // Imposta gli headers
    modifiedRequest = this.normalizeRequestHeaders(modifiedRequest);

    this.coreService.startHttpRunning();

    return next.handle(modifiedRequest).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            this.router.navigate(['/auth/login/'], { relativeTo: this.route });
          } else if (error.status === 503) {
            this.router.navigate(['/auth/maintenance/'], { relativeTo: this.route });
            this.handleError(error);
          } else {
            this.handleError(error);
          }
        }

        // Crea un nuovo errore sotto forma di Observable, senza interrompere il flusso
        // così i componenti lo riconoscono come errore, senza che bugsnag lo rilevi come errore non gestito
        return new Observable<HttpEvent<any>>((observer) => {
          observer.error({
            handled: true, // Flag per sapere che l'errore è stato gestito
            status: error.status,
            ignoreBugsnag: true,
            message: error.error || error.message || 'Errore sconosciuto',
          });
        });
      }),
      finalize(() => this.coreService.stopHttpRunning())
    );
  }

  // region: --- request

  private normalizeRequestHeaders(request: HttpRequest<any>): HttpRequest<any> {
    let modifiedHeaders = new HttpHeaders();
    modifiedHeaders = this.addAuthorizationHeaders(modifiedHeaders);

    return request.clone({
      headers: modifiedHeaders,
    });
  }

  private addAuthorizationHeaders(headers: HttpHeaders): HttpHeaders {
    const token = this.storageService.get(ConfigSession.token);
    if (token) {
      /*
       * In ottica migrazione Lumen > Laravel viene aggiunto l'header Authorization anche se non utilizzato,
       * così all'aggiornamento del client (con la migrazione completata) non si creano problemi all'utente
       */
      headers = headers.set(ConfigSession.token, token); // TODO quando la migrazione verrà completata va rimossa questa riga
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  // endregion

  // region: --- response

  private handleError(errorResponse: HttpErrorResponse) {
    // Caso in cui il server ritorna errori generici, non gestiti dall'app
    if (errorResponse.status === 0 || errorResponse.status === 500 || !errorResponse.error || errorResponse.error.message === '') {
      let errorMessage = errorResponse?.error?.message || errorResponse.message;
      if (environment.production) {
        errorMessage = errorResponse.statusText;
      }

      this.popAlertService.showHttpMessages(errorMessage);
      return;
    }

    if (errorResponse.error) {
      // Caso in cui il server ritorna un errore string (es errori gestiti dall'app)
      if (typeof errorResponse.error === 'string') {
        this.popAlertService.showHttpMessages(errorResponse.error);
        return;
      }

      // Caso in cui il server ritorna un errore per una chiamta che si aspetta un file di download
      if (errorResponse.error instanceof Blob) {
        errorResponse.error.text().then((err) => {
          this.popAlertService.showHttpMessages(err);
        });
        return;
      }

      // Caso in cui il server ritorna un msg d'errore dentro la proprietà message: lo metto dentro un array per poi ciclarlo
      const errors = [];
      errors.push(errorResponse.error.message);

      // Caso in cui il server ritorna un array di errori string (es le validazioni)
      for (const property in errorResponse.error) {
        if (errorResponse.error.hasOwnProperty(property)) {
          const propertyErrors: string[] = errorResponse.error[property];
          if (typeof propertyErrors !== 'string' && propertyErrors.length > 0) {
            propertyErrors.forEach((error) => {
              if (typeof error === 'string') {
                errors.push(error);
              }
            });
          }
        }
      }

      const errorMessage = errors.filter((error) => error).join('<br />');
      this.popAlertService.showErrorMessage(errorMessage);
    }
  }

  // endregion
}
